// Types shared by the cookie-consent system (store, storage, API). See
// docs/cookie-consent.md for the architecture.

/**
 * Categories a visitor can grant or refuse. "necessary" is always on.
 *
 * "preferences" is the only optional one and nothing in TPA uses it yet; it is
 * kept so the banner is ready for the day something optional is added. Analytics
 * and marketing were removed because TPA uses neither (docs/cookie-consent.md
 * explains how to bring one back: add it here, in cookieInventory.ts, in the API
 * model/migration and in ValidateConsentInput).
 */
export const OPTIONAL_CONSENT_CATEGORIES = ['preferences'] as const
export type OptionalConsentCategory = (typeof OPTIONAL_CONSENT_CATEGORIES)[number]
export type ConsentCategory = 'necessary' | OptionalConsentCategory

/** One boolean per optional category - what the visitor chose. */
export type ConsentChoices = Record<OptionalConsentCategory, boolean>

/**
 * How the choice was expressed. Mirrors domain.ConsentAction in the API; it is
 * audit evidence ("one-click accept" and "custom selection" are different
 * expressions of will).
 */
export type ConsentAction = 'accept_all' | 'reject_all' | 'custom' | 'withdraw'

/**
 * The decision as persisted in the browser (localStorage, see
 * services/consentStorage.ts). It is the ENFORCEMENT source of truth: every
 * "may I load this?" question is answered from it, synchronously, without a
 * network call. The server only receives a copy for the audit log.
 */
export interface StoredConsent {
  /** Schema version of this object, so a future change can migrate old values. */
  schema: 1
  /** Random id of this browser. Links its successive decisions without a login. */
  subjectId: string
  /** Cookie-policy version the banner displayed when the visitor decided. */
  policyVersion: string
  action: ConsentAction
  categories: ConsentChoices
  /** ISO-8601 timestamp of the decision (client clock; the server stamps its own). */
  decidedAt: string
  /** ISO-8601 instant after which the choice must be asked again. */
  expiresAt: string
  /** False until the audit log accepted this decision; retried on next load. */
  synced: boolean
}

/** Body of POST /v2/consents. Never includes IP/User-Agent: the server reads those itself. */
export interface RecordConsentPayload {
  subjectId: string
  policyVersion: string
  action: ConsentAction
  categories: ConsentChoices
  language: string
}

/** Body of POST /v2/consents/verify: the decision this browser believes the audit log holds. */
export type VerifyConsentPayload = Pick<
  RecordConsentPayload,
  'subjectId' | 'policyVersion' | 'action'
>
